/**
 * Source Tracker — публичный API.
 *
 * Использование:
 *
 *   import { initSourceTracker, capturePageView, getCurrentAttribution } from '@/lib/source-tracker';
 *
 *   // В Providers (один раз на маунте):
 *   await initSourceTracker();
 *
 *   // На каждой смене страницы (Next.js — через usePathname/useRouter):
 *   capturePageView();
 *
 *   // При submit заказа:
 *   const attribution = getCurrentAttribution();
 *   fetch('/api/orders', { body: JSON.stringify({ ...order, attribution }) });
 */

import { buildPayload, captureClickIds, captureUtm } from './capture';
import { classifyClientSide } from './channels';
// Статический импорт: слушатели ошибок надо ставить ДО любого await, иначе
// ошибки первых сотен миллисекунд (в т.ч. падение гидратации) не ловит никто.
// Цикл index → error-tracker → index безопасен: error-tracker берёт из index
// только captureEvent (function declaration, поднимается при hoisting).
import { initErrorTracker } from './error-tracker';
import { computeFingerprint } from './fingerprint';
import { pingIdentity, sendLog } from './sender';
import {
  getFirstTouch,
  getIdentity,
  getLastTouch,
  setFirstTouchIfAbsent,
  setIdentity,
  setLastTouch,
} from './storage';
import type { AttributionSnapshot } from './types';

// Сколько ждём ответ /api/log/ping на инициализации: зависший ping не должен
// блокировать pageview, web-vitals и остальную инициализацию.
const PING_TIMEOUT_MS = 3000;

// Сессия на сервере живёт 30 минут и ротируется (cookie _az_sid), а клиент
// кэширует session_id в sessionStorage до перезагрузки вкладки. Поэтому раз в
// IDENTITY_TTL_MS активности перезапрашиваем identity — иначе заказ из
// долгоживущей вкладки уедет на протухшую сессию и выпадет из отчётов.
const IDENTITY_TTL_MS = 5 * 60 * 1000;
let lastIdentityPingAt = 0;

/** Перезапрашивает visitor/session id, если кэш устарел. Не блокирует вызывающего. */
function refreshIdentityIfStale(): void {
  // До конца initSourceTracker() identity ставит сам init — второй параллельный
  // ping выдал бы ВТОРУЮ пару cookie и развёл identity с сессией на сервере.
  if (!initialized) return;
  if (Date.now() - lastIdentityPingAt < IDENTITY_TTL_MS) return;
  lastIdentityPingAt = Date.now();  // ставим сразу — чтобы не плодить параллельные ping'и
  pingIdentity()
    .then((id) => {
      lastIdentityPingAt = Date.now();
      // isNew = сервер выдал sid прямо сейчас, потому что cookie протухла.
      // Строку в `sessions` создаёт только POST /api/log/v, а он в этот момент
      // летит ещё с пустой cookie и заведёт сессию с ДРУГИМ sid. Принять такой
      // sid — значит отправить заказ с session_id, которого нет в sessions:
      // конверсия выпадет из отчётов, которые джойнят conversions к sessions.
      // Оставляем прежний id и ждём следующего цикла — тогда cookie устоится
      // и ping вернёт подтверждённый sid с isNew=false.
      if (id && !id.isNew) setIdentity({ visitorId: id.vid, sessionId: id.sid });
    })
    .catch(() => { /* ignore */ });
}

/**
 * Глобальный фильтр — не трекать internal-трафик.
 * Админка засоряет аналитику (свои собственные просмотры, тестовые клики и т.д.).
 */
function isInternalPath(): boolean {
  if (typeof window === 'undefined') return true;
  const path = window.location.pathname;
  return path.startsWith('/admin') || path.startsWith('/api');
}

export type { AttributionSnapshot, Channel, ClickIds, UtmParams } from './types';
export { getFirstTouch, getLastTouch } from './storage';
export { classifyClientSide } from './channels';

let initialized = false;
let initPromise: Promise<void> | null = null;

/**
 * Инициализация трекера. Должна вызываться ОДИН раз на маунте Providers.
 *   - получает visitor_id / session_id от сервера
 *   - вычисляет fingerprint
 *   - захватывает первый pageview
 *
 * Безопасна для многократных вызовов (idempotent).
 */
export async function initSourceTracker(): Promise<void> {
  if (initialized) return;
  if (initPromise) return initPromise;

  initPromise = (async () => {
    if (typeof window === 'undefined') {
      initialized = true;
      return;
    }

    // 1. Error tracker (window.onerror + unhandledrejection + fetch monkey-patch).
    // Ставим ПЕРВЫМ действием, синхронно, до любого await — иначе ошибки,
    // случившиеся пока идёт ping, теряются целиком. Функция идемпотентна.
    try {
      initErrorTracker();
    } catch { /* ignore */ }

    // 2. Pingaем сервер: получаем identity, ставим HttpOnly cookies.
    // Ждём не дольше PING_TIMEOUT_MS, но identity применяем и если ответ
    // пришёл позже таймаута.
    lastIdentityPingAt = Date.now();
    const pingPromise = pingIdentity();
    void pingPromise.then((id) => {
      lastIdentityPingAt = Date.now();
      if (id) setIdentity({ visitorId: id.vid, sessionId: id.sid });
    });
    await Promise.race([
      pingPromise,
      new Promise<null>((resolve) => { setTimeout(() => resolve(null), PING_TIMEOUT_MS); }),
    ]);

    // 3. Считаем fingerprint в фоне (не блокирует pageview)
    computeFingerprint()
      .then((fp) => setIdentity({ fingerprint: fp }))
      .catch(() => { /* ignore */ });

    initialized = true;

    // 4. Web Vitals — TTFB/FCP/LCP/INP/CLS. Подписки висят на window lifecycle,
    // финальные значения шлются автоматически при visibility=hidden (sendBeacon).
    // Только на реальных страницах, не в /admin/*
    if (!isInternalPath()) {
      import('./web-vitals')
        .then(({ initWebVitals }) => initWebVitals())
        .catch(() => { /* ignore */ });
    }

    // 5. Захватываем первый pageview
    capturePageView();
  })();

  return initPromise;
}

/**
 * Реэкспорт logClientError — для использования в catch-блоках:
 *   import { logClientError } from '@/lib/source-tracker';
 *   try { ... } catch (e) { logClientError('Failed to load menu', e); }
 */
export async function logClientError(message: string, error?: Error | unknown): Promise<void> {
  if (typeof window === 'undefined') return;
  if (isInternalPath()) return;  // не логируем ошибки с /admin/*
  const mod = await import('./error-tracker');
  mod.logClientError(message, error);
}

/**
 * Захват pageview — отправка события на сервер + обновление local snapshot.
 */
export function capturePageView(): void {
  if (typeof window === 'undefined') return;
  if (isInternalPath()) return;  // не трекаем /admin/*

  // Сессия могла проротироваться на сервере, пока вкладка жила — обновляем id
  refreshIdentityIfStale();

  const identity = getIdentity();
  const fp = identity.fingerprint || undefined;

  // Собираем snapshot для local storage (для UI и checkout payload)
  const url = window.location.href;
  const ref = document.referrer;
  const utm = captureUtm(url);
  const clickIds = captureClickIds(url);
  const classified = classifyClientSide({ url, referrer: ref, utm, clickIds });

  const snapshot: AttributionSnapshot = {
    visitorId: identity.visitorId,
    sessionId: identity.sessionId,
    channel: classified.channel,
    source: classified.source,
    medium: classified.medium,
    campaign: classified.campaign,
    content: classified.content,
    term: classified.term,
    referrer: ref || null,
    landing: url,
    clickIdType: classified.clickIdType,
    clickIdValue: classified.clickIdValue,
  };

  // First-touch — записываем только если ещё нет
  setFirstTouchIfAbsent(snapshot);

  // Last-touch — перезаписываем ТОЛЬКО при значимом касании (правило GA4
  // «last non-direct»): есть utm-метка, есть click_id или внешний реферер.
  // Обычная навигация внутри сайта иначе затирала бы рекламный снимок на
  // 'direct'/'organic_search', и в заказ уходил бы неверный канал.
  const hasSignal =
    Object.keys(utm).length > 0 ||
    Object.keys(clickIds).length > 0 ||
    (!!classified.referringDomain && classified.channel !== 'direct');
  if (hasSignal || !getLastTouch()) {
    setLastTouch(snapshot);
  }

  // Шлём на сервер (sendBeacon)
  const payload = buildPayload({ evt: 'pageview', fingerprint: fp });
  sendLog(payload);
}

/**
 * Захват custom event'а (например, add_to_cart, checkout_start, scroll_depth).
 */
export function captureEvent(
  name: string,
  props?: Record<string, unknown>,
  options?: { value?: number; currency?: string }
): void {
  if (typeof window === 'undefined') return;
  if (isInternalPath()) return;  // не трекаем /admin/* (включая client_error)

  // Активность без смены роута (чекаут) — тоже повод освежить session_id,
  // иначе order payload уйдёт с протухшей сессией
  refreshIdentityIfStale();

  const identity = getIdentity();
  const fp = identity.fingerprint || undefined;

  const payload = buildPayload({
    evt: 'event',
    evtName: name,
    props,
    value: options?.value,
    currency: options?.currency,
    fingerprint: fp,
  });
  sendLog(payload);
}

/**
 * Получить snapshot текущей атрибуции — для передачи в order payload.
 *
 * Возвращает last-touch + identity. Если в текущей сессии было касание
 * с яркой атрибуцией (UTM/click_id) — оно сохранилось в last-touch.
 */
export function getCurrentAttribution(): AttributionSnapshot {
  const identity = getIdentity();
  const lastTouch = getLastTouch();

  if (lastTouch) {
    return {
      ...lastTouch,
      visitorId: identity.visitorId || lastTouch.visitorId,
      sessionId: identity.sessionId || lastTouch.sessionId,
    };
  }

  return {
    visitorId: identity.visitorId,
    sessionId: identity.sessionId,
    channel: null,
    source: null,
    medium: null,
    campaign: null,
    content: null,
    term: null,
    referrer: null,
    landing: null,
    clickIdType: null,
    clickIdValue: null,
  };
}

/**
 * Получить first-touch — изначальный источник посетителя.
 * Полезно для отображения «Вы пришли с …» и для аналитики LTV by first source.
 */
export function getFirstTouchAttribution(): AttributionSnapshot | null {
  return getFirstTouch();
}
